import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Lock, KeyRound, ShieldCheck } from "lucide-react";
import AppShell from "../components/AppShell";
import ConnectorIcon from "../components/ConnectorIcon";
import api, { formatApiError } from "../lib/api";

const CAP_LABELS = {
  contact_read: "Contact read", contact_update: "Contact update",
  tag_read: "Tag read", tag_write: "Tag write",
  custom_field_read: "Custom fields read", custom_field_write: "Custom fields write",
  pagination: "Pagination", rate_limit_handling: "Rate-limit handling",
};

export default function ConnectCRM() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") === "/onboarding" ? "/onboarding" : "/dashboard";
  const [connectors, setConnectors] = useState([]);
  const [quota, setQuota] = useState(null);
  const [selected, setSelected] = useState(null);
  const [label, setLabel] = useState("");
  const [creds, setCreds] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/connectors"), api.get("/connections/quota")])
      .then(([connectorResponse, quotaResponse]) => {
        setConnectors(connectorResponse.data);
        setQuota(quotaResponse.data);
      })
      .catch(() => {});
  }, []);

  const pick = (c) => {
    if (!["available", "testing"].includes(c.status)) return;
    setSelected(c);
    setLabel("");
    setCreds({});
  };

  const submit = async (e) => {
    e.preventDefault();
    const connectionName = label.trim();
    if (!connectionName) {
      toast.error("Connection name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/connections", {
        connector_type: selected.connector_id,
        label: connectionName,
        credentials: creds,
      });
      toast.success(`Connected: ${data.detail || data.label}`);
      navigate(returnTo);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

  return (
    <AppShell>
      <button onClick={() => (selected ? setSelected(null) : navigate(returnTo))}
              data-testid="connect-back-button"
              className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      {!selected ? (
        <>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Connect a CRM</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Choose the CRM you want to clean. CleanIQ will guide you through the connection details needed for that provider.
          </p>

          {quota && <div className={`mt-5 border p-4 text-sm ${quota.at_limit ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-600"}`} data-testid="connection-quota">
            <div><span className="font-semibold capitalize">{quota.plan}</span> CRM connections: {quota.used} of {quota.limit}.
              {quota.at_limit && <span> Disconnect a CRM or upgrade before connecting another.</span>}
            </div>
            {quota.contact_limit > 0 && <div className="mt-1" data-testid="contact-quota">
              Active contacts counted toward this plan: {quota.contacts_used.toLocaleString()} of {quota.contact_limit.toLocaleString()}.
              {quota.contacts_over_limit > 0 && <span> Upgrade or disconnect a CRM before another scan.</span>}
            </div>}
          </div>}

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {connectors.filter((c) => c.connector_id !== "generic_rest").map((c) => {
              const available = c.status === "available";
              const testing = c.status === "testing";
              const connectable = available || testing;
              return (
                <button
                  key={c.connector_id}
                  data-testid={`connector-option-${c.connector_id}`}
                  onClick={() => pick(c)}
                  disabled={!connectable || quota?.at_limit}
                  className={`flex flex-col items-start border bg-white p-5 text-left transition-colors ${
                    connectable && !quota?.at_limit ? "border-border hover:border-slate-900" : "cursor-not-allowed border-dashed border-border opacity-70"
                  }`}
                >
                  <div className="flex w-full items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center border border-border bg-slate-50">
                      <ConnectorIcon name={c.icon} className="h-5 w-5 text-slate-700" />
                    </div>
                    {available ? (
                      <span className="border border-health-good/40 bg-emerald-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-health-good">Available</span>
                    ) : testing ? (
                      <span className="border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-700">Testing</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                        <Lock className="h-3 w-3" /> Coming soon
                      </span>
                    )}
                  </div>
                  <div className="mt-4 font-heading text-lg font-bold text-slate-900">{c.display_name}</div>
                  <p className="mt-1 text-sm text-slate-500">{c.description}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {c.auth_type === "oauth" ? <ShieldCheck className="h-3 w-3" /> : <KeyRound className="h-3 w-3" />}
                      {c.auth_type.replaceAll("_", " ")}
                    </span>
                    {Object.entries(c.capabilities).filter(([, v]) => v).slice(0, 3).map(([k]) => (
                      <span key={k} className="border border-border px-1.5 py-0.5 font-mono text-[10px] text-slate-400">{CAP_LABELS[k] || k}</span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          <section data-testid="crm-request-message" className="mt-6 border border-slate-200 bg-slate-50 p-5">
            <h2 className="font-heading text-lg font-bold text-slate-900">Don’t see your CRM?</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Tell CleanIQ Support which CRM you use, and we’ll confirm compatibility and next steps.
            </p>
          </section>
        </>
      ) : (
        <div className="max-w-xl">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center border border-border bg-slate-50">
              <ConnectorIcon name={selected.icon} className="h-6 w-6 text-slate-700" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Connect {selected.display_name}</h1>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Auth type: {selected.auth_type.replaceAll("_", " ")}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5 border border-border bg-white p-6">
            <div>
              <label htmlFor="connection-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Connection name</label>
              <input
                id="connection-name"
                data-testid="connection-label-input"
                required
                maxLength={120}
                placeholder="Cain Family Insurance"
                aria-describedby="connection-name-help"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className={inputCls}
              />
              <p id="connection-name-help" className="mt-1.5 text-xs leading-5 text-slate-500">
                Use the business or CRM location name shown on your dashboard.
              </p>
            </div>

            {selected.auth_fields.length === 0 && selected.auth_type === "none" && (
              <div className="flex items-center gap-2 border border-border bg-slate-50 p-3 text-sm text-slate-600">
                <Check className="h-4 w-4 text-health-good" /> No credentials required for this connector.
              </div>
            )}

            {selected.auth_fields.map((f) => (
              <div key={f.name}>
                <label htmlFor={`credential-${f.name}`} className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {f.label}{f.required && <span className="text-health-critical"> *</span>}
                </label>
                <input
                  id={`credential-${f.name}`}
                  aria-describedby={f.help ? `credential-help-${f.name}` : undefined}
                  data-testid={`cred-input-${f.name}`}
                  type={f.type === "password" ? "password" : "text"}
                  required={f.required}
                  placeholder={f.placeholder}
                  value={creds[f.name] || ""}
                  onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })}
                  className={inputCls}
                />
                {f.help && <p id={`credential-help-${f.name}`} className="mt-1 text-xs text-slate-400">{f.help}</p>}
              </div>
            ))}

            <div className="flex items-center gap-2 border-t border-border pt-4">
              <button data-testid="submit-connection-button" type="submit" disabled={submitting}
                      className="bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60">
                {submitting ? "Testing connection…" : "Test & Connect"}
              </button>
              <span className="font-mono text-[11px] text-slate-400">Read-only. Credentials are encrypted at rest.</span>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}
